import { BuildingsIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/Badge';
import { Checkbox } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';

interface SourceFolderItemProps {
    id: string;
    name: string;
    totalCount: number;
    selectedCount: number;
    isActive: boolean;
    onClick: () => void;
    onSelectFolder?: (folderId: string) => void;
    isLoading?: boolean;
    checked?: boolean | 'indeterminate';
    selectDisabled?: boolean;
    showCounter?: boolean;
    isSharedByOwner?: boolean;
}

export function SourceFolderItem({
    id,
    name,
    totalCount,
    selectedCount,
    isActive,
    onClick,
    onSelectFolder,
    isLoading = false,
    checked,
    selectDisabled = false,
    showCounter = true,
    isSharedByOwner = false,
}: SourceFolderItemProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const resolvedChecked: boolean | 'indeterminate' =
        checked !== undefined
            ? checked
            : totalCount > 0 && selectedCount === totalCount
              ? true
              : false;

    const stopPropagation = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const rowSurfaceClassName = cn(
        'px-3 py-2 rounded transition-colors flex items-center gap-2',
        isActive
            ? 'bg-grey-600 text-white-100'
            : 'text-grey-200 hover:bg-grey-600/50 hover:text-white-100',
    );

    const nameContent = (
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="text-sm-medium truncate min-w-0">{name}</span>
            {isSharedByOwner && (
                <Badge
                    variant="iconSquare"
                    className={cn('shrink-0', isActive && 'bg-grey-500')}
                >
                    <BuildingsIcon
                        className="size-3 text-grey-200"
                        weight="fill"
                        aria-label={t('folders.sharedBadge')}
                    />
                </Badge>
            )}
        </span>
    );

    const counter = showCounter ? (
        <span className="flex items-center gap-1.5 shrink-0">
            <span className="text-xs text-grey-300">
                {isLoading ? '...' : `${selectedCount}/${totalCount}`}
            </span>
        </span>
    ) : null;

    if (!onSelectFolder) {
        return (
            <button
                type="button"
                data-testid={`sources-menu-folder-${id}`}
                onClick={onClick}
                className={cn(
                    rowSurfaceClassName,
                    'w-full text-left cursor-pointer border-0 bg-transparent font-inherit',
                )}
            >
                {nameContent}
                {counter}
            </button>
        );
    }

    return (
        <div
            data-testid={`sources-menu-folder-${id}`}
            className={rowSurfaceClassName}
        >
            <div onClick={stopPropagation} className="shrink-0">
                <Checkbox
                    checked={resolvedChecked}
                    disabled={selectDisabled}
                    onChange={() => {
                        if (selectDisabled) return;
                        onSelectFolder(id);
                    }}
                />
            </div>
            <button
                type="button"
                onClick={onClick}
                className={cn(
                    'min-w-0 flex-1 flex items-center gap-2 text-left cursor-pointer',
                    'border-0 bg-transparent p-0 font-inherit text-inherit',
                )}
            >
                {nameContent}
                {counter}
            </button>
        </div>
    );
}
